// src/utils/device.ts
//
// What kind of device this is, for one question: can it run our installers? The writing
// app ships for Windows and Mac only. A phone, a tablet, a Chromebook or a Linux machine
// gets its own answer on the Downloads page (#724, #733), the download link by email when
// an account is made on one, and its own line on the empty bookshelf (#938).
//
// Both of our first customers arrived on a phone. iOS used to be bucketed into "mac",
// which handed an iPhone a .pkg; Linux fell through to "windows" and was handed a .exe.

export type DeviceKind = 'phone' | 'tablet' | 'chromebook' | 'linux' | 'computer';

export function deviceKind(): DeviceKind {
  if (typeof navigator === 'undefined') return 'computer';
  const ua = navigator.userAgent || '';
  // First: a Chromebook is a laptop, whatever else its user agent says. Case-sensitive,
  // because /cros/i also matches the "Microsoft" in Windows Phone's user agent.
  if (/\bCrOS\b/.test(ua)) return 'chromebook';
  if (/iPhone|iPod|Windows Phone/i.test(ua)) return 'phone';
  // Android phones say Mobile; Android tablets don't.
  if (/Android/i.test(ua)) return /Mobile/i.test(ua) ? 'phone' : 'tablet';
  if (/iPad/i.test(ua)) return 'tablet';
  // iPadOS asks for desktop sites by default and reports itself as a Mac. A desktop Mac
  // has no touch points, so they tell the two apart.
  const touch = typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  if (/Macintosh/i.test(ua) && touch) return 'tablet';
  // Android reports Linux too, which is why this comes after it.
  if (/\bLinux\b|X11/i.test(ua)) return 'linux';
  return 'computer';
}

/** Whether this kind of device runs the Windows or Mac installer. */
export function runsDesktopApp(kind: DeviceKind = deviceKind()): boolean {
  return kind === 'computer';
}
